import React, { useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { BRAND } from "themes";

type Props = {
  open: boolean;
  onClose: () => void;
  url?: string | null;
  title?: string;
};

const isImageUrl = (url: string) =>
  /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|$)/i.test(url) ||
  url.startsWith("blob:") ||
  url.startsWith("data:image/");

const isPdfUrl = (url: string) =>
  /\.pdf(\?|$)/i.test(url) || url.includes("application/pdf");

const fileNameFromUrl = (url: string) => {
  try {
    const path = decodeURIComponent(url.split("?")[0] || "");
    const parts = path.split("/");
    return parts[parts.length - 1] || "Document";
  } catch {
    return "Document";
  }
};

const DocumentPreviewModal = ({
  open,
  onClose,
  url,
  title = "Document preview",
}: Props) => {
  const safeUrl = `${url || ""}`.trim();
  const kind = useMemo(() => {
    if (!safeUrl) return "empty";
    if (isImageUrl(safeUrl)) return "image";
    if (isPdfUrl(safeUrl)) return "pdf";
    return "file";
  }, [safeUrl]);

  const fileName = safeUrl ? fileNameFromUrl(safeUrl) : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "22px",
          border: `1px solid ${BRAND.border}`,
          boxShadow: "0 24px 50px -20px rgba(27, 19, 64, 0.35)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          pr: 1.5,
          color: BRAND.ink,
          fontWeight: 700,
          fontSize: 18,
          letterSpacing: "-0.02em",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 18 }}>
            {title}
          </Typography>
          {fileName ? (
            <Typography
              sx={{
                mt: 0.5,
                color: BRAND.muted,
                fontSize: 13,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close preview"
          sx={{
            width: 36,
            height: 36,
            borderRadius: "12px",
            border: `1px solid ${BRAND.border}`,
            color: BRAND.ink,
            "&:hover": { backgroundColor: "#F7F5FF" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          bgcolor: BRAND.track,
          minHeight: { xs: 320, sm: 480 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!safeUrl || kind === "empty" ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <InsertDriveFileOutlinedIcon
              sx={{ fontSize: 42, color: BRAND.muted, mb: 1 }}
            />
            <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>
              No document available
            </Typography>
          </Box>
        ) : kind === "image" ? (
          <Box
            component="img"
            src={safeUrl}
            alt={fileName || "Document"}
            sx={{
              maxWidth: "100%",
              maxHeight: { xs: 360, sm: 560 },
              objectFit: "contain",
              display: "block",
              m: "auto",
              p: 2,
            }}
          />
        ) : kind === "pdf" ? (
          <Box
            component="iframe"
            title={fileName || "PDF preview"}
            src={safeUrl}
            sx={{
              width: "100%",
              height: { xs: 360, sm: 560 },
              border: 0,
              bgcolor: "#fff",
            }}
          />
        ) : (
          <Box sx={{ textAlign: "center", p: 4, maxWidth: 420 }}>
            <InsertDriveFileOutlinedIcon
              sx={{ fontSize: 42, color: BRAND.primary, mb: 1.5 }}
            />
            <Typography
              sx={{ color: BRAND.ink, fontWeight: 700, mb: 1, fontSize: 16 }}
            >
              Preview not available for this file type
            </Typography>
            <Typography sx={{ color: BRAND.muted, fontWeight: 500, mb: 2 }}>
              Open the document in a new tab to view or download it.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<OpenInNewIcon />}
              href={safeUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: "none", borderRadius: "12px", fontWeight: 700 }}
            >
              Open document
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
        {safeUrl ? (
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            href={safeUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              fontWeight: 600,
              borderColor: BRAND.border,
              color: BRAND.ink,
            }}
          >
            Open in new tab
          </Button>
        ) : null}
        <Button
          onClick={onClose}
          variant="contained"
          color="secondary"
          sx={{ textTransform: "none", borderRadius: "12px", fontWeight: 700 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewModal;
